import { useDarkMode } from "@/hooks/useDarkMode";
import { RotatingLines, ThreeDots } from "react-loader-spinner";
import LOGO from "../../assets/images/Logo_Solo.svg";
import FocusTrap from "focus-trap-react";
import { useRef, useEffect } from "react";

const Loader = ({
  includeGoogleFallback = false,
  "aria-label": ariaLabel,
}: {
  includeGoogleFallback?: boolean;
  "aria-label"?: string;
}) => {
  const isDark = useDarkMode();
  const loaderRef = useRef<HTMLDivElement>(null);

  // Focus the loader when it mounts to ensure screen readers announce it
  // Note: Focus timing is appropriate - occurs after mount but before user interaction
  // The focus trap (FocusTrap wrapper below) ensures focus stays within the loader
  useEffect(() => {
    if (loaderRef.current) {
      loaderRef.current.focus();
    }
  }, []);

  if (includeGoogleFallback) {
    return (
      <FocusTrap
        focusTrapOptions={{
          allowOutsideClick: false,
          initialFocus: false,
          fallbackFocus: () => loaderRef.current || document.body,
        }}
      >
        <div 
          ref={loaderRef}
          className="fixed left-0 right-0 bottom-0 top-0 flex items-center justify-center bg-black bg-opacity-30 z-[1002] w-full h-full px-4" 
          role="status" 
          aria-live="polite" 
          aria-busy="true"
          aria-label={ariaLabel || "Loading, please wait"}
          tabIndex={-1}
        >
          <div className="bg-bg-brand-secondary-default flex-col justify-center items-center rounded-[12px] p-[24px] w-full max-w-[400px]">
            <div className="mb-4 self-center items-center justify-center flex">
              <img src={LOGO} alt="" width={40} aria-hidden="true" />
            </div>
            <p className="text-fg-brand-strong title-s-semibold text-center">
              Finding more places nearby
            </p>
            <p className="text-fg-neutral-subtle label-m-medium text-center">
              Please wait a moment while we refresh your search
            </p>
            <br />
            <div className="self-center items-center justify-center flex mb-4">
              <RotatingLines
                visible={true}
                width="40"
                ariaLabel="rotating-lines-loading"
                strokeColor={isDark ? "#D3D3D9" : "#151A36"}
              />
            </div>
            <p className="text-fg-neutral-subtle label-m-medium text-center">
              Updating your map view
            </p>
          </div>
        </div>
      </FocusTrap>
    );
  }
  return (
    <FocusTrap
      focusTrapOptions={{
        allowOutsideClick: false,
        initialFocus: false,
        fallbackFocus: () => loaderRef.current || document.body,
      }}
    >
      <div 
        ref={loaderRef}
        className="fixed left-0 right-0 bottom-0 top-0 flex items-center justify-center bg-black bg-opacity-30 z-[1002] w-full h-full" 
        role="status" 
        aria-live="polite" 
        aria-busy="true"
        aria-label={ariaLabel || "Loading, please wait"}
        tabIndex={-1}
      >
        {/* Colours come from the `dark` class, not `useDarkMode()`: this is
            the loader in the server-rendered first paint, and its markup is
            not patched on hydration. The pill follows the theme too — the
            light dots were near-invisible on the white pill. */}
        <div className="w-[80px] h-[50px] bg-white dark:bg-bg-brand-secondary-default flex justify-center items-center rounded-full">
          <ThreeDots
            visible={true}
            height="35"
            width="35"
            color="currentColor"
            radius="9"
            ariaLabel="three-dots-loading"
            wrapperStyle={{}}
            wrapperClass="text-[#151A36] dark:text-[#D3D3D9]"
          />
        </div>
      </div>
    </FocusTrap>
  );
};

export default Loader;
